<script lang="ts">
	import { type get_map } from 'shared/json_api/get_related'
	import { cubicOut } from 'svelte/easing'
	import Card from './Card.svelte'
	import { onMount } from 'svelte'

	export let storefront_menu_items: ResourceTypes[`storefront_menu_item`][] = []
	export let active_menu_id: string | null = null
	export let included_map: ReturnType<typeof get_map>
	export let window_width: number
	export let menu_href_prefix: string

	const breadcrumbs = (menu_item: ResourceTypes[`storefront_menu_item`] | undefined): ResourceTypes[`storefront_menu_item`][] => {
		// don't include root parent in desktop since it's already visible
		if (!menu_item || (!mobile && !menu_item.relationships.parent_storefront_menu_item?.data?.id)) {
			return []
		}

		const parent = storefront_menu_items.find(c => c.id === menu_item.relationships.parent_storefront_menu_item?.data?.id)

		return parent ? [ ...breadcrumbs(parent), menu_item ] : [ menu_item ]
	}

	const card_columns = 2
	const card_aspect_ratio = 0.33
	const desktop_card_width = (1040 - 20) / card_columns
	const no_thumbnail_height = 20
	const thumbnail_height = desktop_card_width * card_aspect_ratio

	$: active_menu_item = storefront_menu_items.find(item => item.id === active_menu_id)
	$: menu_item_chain = breadcrumbs(active_menu_item)
	$: current_items = storefront_menu_items.filter(item => item.relationships.parent_storefront_menu_item?.data?.id === (active_menu_id === `mobile` ? undefined : active_menu_id))
	$: mobile = window_width <= 800
	$: grid_rows = Math.ceil(current_items.length / card_columns)

	let previous_chain = []

	function disappear(node: HTMLElement) {
		node.style.display = `none`
		return {}
	}

	function slide_in(_node: HTMLElement) {
		const distance = previous_chain.length - menu_item_chain.length
		const direction = distance < 0 ? 1 : -1
		previous_chain = menu_item_chain

		return {
			delay: 0,
			duration: distance === 0 ? 0 : 200,
			easing: cubicOut,
			css: (t: number, u: number) => `transform: translate(${ (u * direction) * 100 }%, 0);`,
		}
	}

	onMount(() => {
		document.body.classList.add(`submenu_open`)

		return () => {
			document.body.classList.remove(`submenu_open`)
		}
	})
</script>

<div class="submenu_container">
	<!-- svelte-ignore a11y_invalid_attribute a11y_consider_explicit_label -->
	<a class="elsewhere" href="#"></a>

	<div class="submenu">
		{#if menu_item_chain.length || (mobile && active_menu_id !== `mobile`)}
			<div class="breadcrumbs">
				{#if mobile && active_menu_id !== `mobile`}
					<a href="{menu_href_prefix}mobile">
						Top
					</a>
					{#if menu_item_chain.length}
						<svg xmlns="http://www.w3.org/2000/svg" width="6" height="12" viewBox="10 0 2 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="square" stroke-linejoin="miter"><path d="M8 6l6 6-6 6"/></svg>
					{/if}
				{/if}

				{#each menu_item_chain.filter(item => !item.attributes.is_section) as item, i}
					<a href="{menu_href_prefix}{item.id}">
						{item.attributes.name}
					</a>

					{#if i < menu_item_chain.length - 1}
						<svg xmlns="http://www.w3.org/2000/svg" width="6" height="12" viewBox="10 0 2 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="square" stroke-linejoin="miter"><path d="M8 6l6 6-6 6"/></svg>
					{/if}
				{/each}
			</div>
		{/if}

		{#each [ active_menu_id ] as _for_the_transition (_for_the_transition)}
			<div in:slide_in out:disappear style="display: flex; flex-direction: column; gap: 10px;">
				{#if current_items.filter(item => item.attributes.is_section).length > 0}
					<div class="sections">
						{#each current_items.filter(item => item.attributes.is_section) as item}
							{@const section_items = storefront_menu_items.filter(sub_item => sub_item.relationships.parent_storefront_menu_item?.data?.id === item.id)}
							{@const needs_two_columns = section_items.length > 10}
							{@const show_image = section_items.some(item => item.attributes.image_url)}
							<div class="section {needs_two_columns ? `span_two` : ``}">
								<div class="section_title">{item.attributes.name}</div>
								<div class="grid {needs_two_columns ? `two_columns` : ``}" style="grid-template-rows: repeat({Math.floor(section_items.length / (needs_two_columns ? 2 : 1))}, minmax({show_image ? thumbnail_height : no_thumbnail_height}px, auto));">
									{#each section_items as sub_item}
										<Card item={sub_item} {storefront_menu_items} {included_map} {menu_href_prefix} {show_image} />
									{/each}
								</div>
							</div>
						{/each}
					</div>
				{/if}

				{#if current_items.filter(item => !item.attributes.is_section).length > 0}
					{@const show_image = current_items.some(item => item.attributes.image_url)}
					<div class="grid" style="grid-template-rows: repeat({grid_rows}, minmax({show_image ? thumbnail_height : no_thumbnail_height}px, auto));">
						{#each current_items.filter(item => !item.attributes.is_section) as item}
							<Card item={item} {storefront_menu_items} {included_map} {menu_href_prefix} {show_image} />
						{/each}
					</div>
				{/if}
			</div>
		{/each}
	</div>
</div>

<style>
	/* blur out woocommerce content when submenu is open */
	:global(body.submenu_open > .container) {
		filter: blur(10px);
	}

	/* prevent scroll when menu is open */
	:global(body.submenu_open) {
		overflow: hidden;
		height: 100vh;
	}

	.elsewhere {
		position: fixed;
		top: var(--header-height);
		left: 0;
		right: 0;
		bottom: 0;
		z-index: 90;
		cursor: default;
		background-color: #0006;
	}

	.submenu_container {
		position: absolute;
		background-color: #000;
		top: var(--header-height);
		left: 0;
		right: 0;
		overflow-y: auto;
		overflow-x: hidden;
		z-index: 100;
	}

	.submenu {
		max-width: 1040px;
		margin: 0 auto;
		padding: 0 0 20px 0;
		position: relative;
		z-index: 100;
	}

	.breadcrumbs {
		display: flex;
		align-items: center;
		flex-wrap: wrap;
		gap: 10px;
		padding: 10px;
		font-size: 16px;
		line-height: 1;
		display: inline-flex;
		text-transform: uppercase;
		font-family: 'Protipo Compact', sans-serif;
		margin-bottom: 10px;
		color: #999;
		letter-spacing: 1px;
	}

	.breadcrumbs a {
		text-decoration: none;
		font-weight: 400;
		color: #999;
	}

	.breadcrumbs a:last-child {
		color: white;
	}

	.sections {
		display: grid;
		grid-template-columns: repeat(3, 1fr);
		gap: 30px;
	}

	.section_title {
		font-size: 1.2rem;
		font-weight: 500;
		color: #999;
		padding: 10px;
		line-height: 1;
		border-bottom: 1px solid #999;
		margin-bottom: 12px;
		text-transform: uppercase;
		font-family: 'Protipo Narrow', sans-serif;
	}

	.grid {
		display: grid;
		grid-template-columns: repeat(2, 1fr);
		grid-auto-flow: column;
		gap: 10px;
	}

	.section {
		grid-column: span 1;
	}

	.section.span_two {
		grid-column: span 2;
	}

	.section .grid {
		grid-template-columns: repeat(1, 1fr);
	}

	.section .grid.two_columns {
		grid-template-columns: repeat(2, 1fr);
	}

	@media(width <= 800px) {
		.submenu_container {
			height: calc(100dvh - var(--header-height));
		}
		.submenu {
			padding: 0 10px 10px 10px;
		}
		.grid {
			display: flex;
			flex-direction: row;
			flex-wrap: wrap;
		}
		.sections {
			display: flex;
			flex-direction: column;
			gap: 20px;
		}
		.section .grid {
			display: flex;
			flex-direction: row;
			flex-wrap: wrap;
		}
	}
</style>
